import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_brew-business-value/artifacts/t1741byz_Colour%20Landscape%20Logo.png";
const BADGE_URL = "https://customer-assets.emergentagent.com/job_brew-business-value/artifacts/04o7d811_australia-sbest-small-business-broker-badge.png";

const australianStates = [
    { value: "NSW", label: "New South Wales" },
    { value: "VIC", label: "Victoria" },
    { value: "QLD", label: "Queensland" },
    { value: "SA", label: "South Australia" },
    { value: "WA", label: "Western Australia" },
    { value: "TAS", label: "Tasmania" },
    { value: "NT", label: "Northern Territory" },
    { value: "ACT", label: "Australian Capital Territory" }
];

const sellingOptions = [
    { value: "yes", label: "Yes, planning to sell" },
    { value: "maybe", label: "Maybe, exploring options" },
    { value: "curious", label: "Just curious about value" }
];

const LeadCaptureScreen = ({ onSubmit, isSubmitting }) => {
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        business_name: "",
        email: "",
        phone: "",
        state: "",
        planning_to_sell: "",
        consent: true  // Always true now, checkbox removed
    });

    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.first_name.trim()) newErrors.first_name = "First name is required";
        if (!formData.last_name.trim()) newErrors.last_name = "Last name is required";
        if (!formData.business_name.trim()) newErrors.business_name = "Business name is required";
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email";
        }
        if (!formData.phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else if (!/^[\d\s+()-]{8,}$/.test(formData.phone)) {
            newErrors.phone = "Please enter a valid phone number";
        }
        if (!formData.state) newErrors.state = "Please select your state";
        if (!formData.planning_to_sell) newErrors.planning_to_sell = "Please select an option";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSubmit(formData);
        } else {
            toast.error("Please fill in all required fields");
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <div className="min-h-screen bg-surface flex flex-col" data-testid="lead-capture-screen">
            {/* Header */}
            <header className="w-full py-4 px-6 bg-white border-b border-gray-100">
                <div className="max-w-lg mx-auto flex items-center justify-center">
                    <img 
                        src={LOGO_URL} 
                        alt="Bonza" 
                        className="h-10 object-contain"
                    />
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center py-8 px-6">
                <motion.div
                    className="w-full max-w-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {/* Success Badge */}
                    <div className="flex justify-center mb-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Valuation Ready</span>
                        </div>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-100">
                        <div className="text-center mb-8">
                            <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                                Your Café Valuation Is Ready
                            </h1>
                            <p className="text-text-secondary">
                                Enter your details to unlock your results
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {/* Name Fields */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name">First Name *</Label>
                                    <Input
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={(e) => handleChange("first_name", e.target.value)}
                                        className={`${errors.first_name ? "border-red-500" : ""}`}
                                        placeholder="John"
                                        data-testid="input-first-name"
                                    />
                                    {errors.first_name && (
                                        <p className="text-xs text-red-500">{errors.first_name}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name">Last Name *</Label>
                                    <Input
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={(e) => handleChange("last_name", e.target.value)}
                                        className={`${errors.last_name ? "border-red-500" : ""}`}
                                        placeholder="Smith"
                                        data-testid="input-last-name"
                                    />
                                    {errors.last_name && (
                                        <p className="text-xs text-red-500">{errors.last_name}</p>
                                    )}
                                </div>
                            </div>

                            {/* Business Name */}
                            <div className="space-y-2">
                                <Label htmlFor="business_name">Business/Café Name *</Label>
                                <Input
                                    id="business_name"
                                    value={formData.business_name}
                                    onChange={(e) => handleChange("business_name", e.target.value)}
                                    className={`${errors.business_name ? "border-red-500" : ""}`}
                                    placeholder="My Café"
                                    data-testid="input-business-name"
                                />
                                {errors.business_name && (
                                    <p className="text-xs text-red-500">{errors.business_name}</p>
                                )}
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange("email", e.target.value)}
                                    className={`${errors.email ? "border-red-500" : ""}`}
                                    placeholder="john@example.com"
                                    data-testid="input-email"
                                />
                                {errors.email && (
                                    <p className="text-xs text-red-500">{errors.email}</p>
                                )}
                            </div>

                            {/* Phone */}
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number *</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleChange("phone", e.target.value)}
                                    className={`${errors.phone ? "border-red-500" : ""}`}
                                    placeholder="04XX XXX XXX"
                                    data-testid="input-phone"
                                />
                                {errors.phone && (
                                    <p className="text-xs text-red-500">{errors.phone}</p>
                                )}
                            </div>

                            {/* State */}
                            <div className="space-y-2">
                                <Label>Which state are you located in? *</Label>
                                <Select
                                    value={formData.state}
                                    onValueChange={(value) => handleChange("state", value)}
                                >
                                    <SelectTrigger 
                                        className={`${errors.state ? "border-red-500" : ""}`}
                                        data-testid="select-state"
                                    >
                                        <SelectValue placeholder="Select your state" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {australianStates.map((state) => (
                                            <SelectItem key={state.value} value={state.value}>
                                                {state.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.state && (
                                    <p className="text-xs text-red-500">{errors.state}</p>
                                )}
                            </div>

                            {/* Planning to Sell */}
                            <div className="space-y-2">
                                <Label>Are you planning to sell in the next 12 months? *</Label>
                                <Select
                                    value={formData.planning_to_sell}
                                    onValueChange={(value) => handleChange("planning_to_sell", value)}
                                >
                                    <SelectTrigger 
                                        className={`${errors.planning_to_sell ? "border-red-500" : ""}`}
                                        data-testid="select-planning"
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {sellingOptions.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.planning_to_sell && (
                                    <p className="text-xs text-red-500">{errors.planning_to_sell}</p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-primary hover:bg-primary/90 text-white py-6 rounded-xl font-semibold text-lg shadow-lg shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5"
                                data-testid="submit-lead-btn"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    "Show My Café Valuation"
                                )}
                            </Button>
                        </form>

                        {/* Security Note */}
                        <div className="flex items-center justify-center gap-2 mt-6 text-sm text-text-muted">
                            <Lock className="w-4 h-4" />
                            <span>Your information is secure and confidential</span>
                        </div>
                        
                        {/* Australia's Best Badge */}
                        <div className="flex justify-center mt-6">
                            <img 
                                src={BADGE_URL}
                                alt="Australia's Best Small Business Broker"
                                className="h-20 object-contain"
                            />
                        </div>
                    </div>
                </motion.div>
            </main>
        </div>
    );
};

export default LeadCaptureScreen;
